import {
  withCompInfo,
  createComponentMapperModel,
} from '@wix/editor-elements-integrations';
import {
  MenuCSSVars,
  SubmenuCSSVars,
  SubmenuBoxCSSVars,
  SubsubmenuBoxCSSVars,
  MenuItemCSSVars,
  StylableHorizontalMenuDefinition,
  StylableHorizontalMenuProps,
  StylableHorizontalMenuCSSVars,
  StylableHorizontalMenuPropsCarmiData,
  CompLayout,
} from '../StylableHorizontalMenu.types';
import { parseValueFromCss, parseFlyoutAlignment } from './utils/parseCss';

const isHorizontallyDocked = (layout: CompLayout): boolean =>
  !!(layout.docked?.right && layout.docked?.left);

export const props = withCompInfo<
  StylableHorizontalMenuProps,
  StylableHorizontalMenuDefinition,
  StylableHorizontalMenuPropsCarmiData
>()(
  ['compProps', 'compLayout', 'compStylableClass', 'styleProperties'],
  (
    { compProps, compLayout, compStylableClass, styleProperties },
    carmiProps,
  ) => {
    const { menuMode, submenuMode } = compProps;
    const cssText = styleProperties['st-css'];

    return {
      menuMode,
      submenuMode,
      items: carmiProps.items,
      isStretched: isHorizontallyDocked(compLayout),
      className: compStylableClass,
      flyoutAlignment: parseFlyoutAlignment(cssText),
      isHeightAuto: menuMode === 'wrap',
    };
  },
);

export const css = withCompInfo<
  StylableHorizontalMenuCSSVars,
  StylableHorizontalMenuDefinition
>()(
  ['compProps', 'styleProperties'],
  ({
    compProps: { menuMode, submenuMode },
    styleProperties,
  }): StylableHorizontalMenuCSSVars => {
    const cssText = styleProperties['st-css'];
    const isScrollable = menuMode === 'scroll';
    const isFlyout = submenuMode === 'flyout';

    const menuCSSVars: MenuCSSVars = {
      '--menu-flex-wrap': isScrollable ? 'nowrap' : 'wrap',
    };

    const submenuCSSVars: SubmenuCSSVars = {
      ...(!isFlyout && {
        '--category-spacing': -parseValueFromCss(
          cssText,
          'columnsLayout::flex',
          'margin-bottom',
          0,
        ),
      }),
    };

    const isSubmenuStretched = submenuMode === 'columnStretched';

    const submenuBoxCSSVars: SubmenuBoxCSSVars = {
      '--submenu-box-position': isFlyout ? 'fixed' : 'absolute',
      ...(!isFlyout && {
        '--submenu-box-left': 0,
        '--submenu-box-right': 0,
        ...(!isSubmenuStretched && {
          '--submenu-box-max-width': '980px',
        }),
      }),
    };

    const subsubmenuBoxCSSVars: SubsubmenuBoxCSSVars = {
      '--subsubmenu-box-opacity': isFlyout ? 0 : 1,
      '--subsubmenu-box-pointer-events': isFlyout ? 'none' : 'inherit',
      '--subsubmenu-box-position': isFlyout ? 'absolute' : 'static',
      '--subsubmenu-box-show-animation': isFlyout ? 1 : 0,
    };

    const menuItemCSSVars: MenuItemCSSVars = {
      '--white-space': isFlyout ? 'nowrap' : 'normal',
      '--label-word-wrap': isFlyout ? 'normal' : 'break-word',
    };

    return {
      ...menuCSSVars,
      ...submenuCSSVars,
      ...submenuBoxCSSVars,
      ...subsubmenuBoxCSSVars,
      ...menuItemCSSVars,
    };
  },
);

export default createComponentMapperModel({ css, props });
